import { useState, useEffect } from 'react'
import { useParams, Link } from 'react-router-dom'
import { useSupabase } from '@/hooks/useSupabase'
import type { Tarea, Proyecto, TareaEvidencia, ConfigTarifaCliente } from '@/lib/types'
import { formatDate, formatCurrency, r2 } from '@/lib/utils'
import TareaModal from '@/components/proyectos/TareaModal'
import { PageHeader } from '@/components/ui'
import {
  IconArrowLeft, IconEdit, IconClock, IconRefresh, IconCheckCircle, IconBan,
  IconArrowDown, IconArrowRight, IconArrowUp, IconAlertCircle,
  IconCalendar, IconDollar, IconTimer, IconTag, IconMessageSquare,
  IconPaperclip, IconMail, IconXCircle, IconAlertTriangle,
  IconFile, IconImage, IconLock, IconBarChart,
} from '@/components/icons/Icons'

const ESTADO_CONFIG: Record<string, { label: string; icon: React.ReactNode; color: string; bg: string }> = {
  pendiente: { label: 'Pendiente', icon: <IconClock size={14} />, color: 'var(--text-muted)', bg: 'color-mix(in srgb, var(--text-muted) 12%, transparent)' },
  en_progreso: { label: 'En Progreso', icon: <IconRefresh size={14} />, color: 'var(--info)', bg: 'color-mix(in srgb, var(--info) 12%, transparent)' },
  completada: { label: 'Completada', icon: <IconCheckCircle size={14} />, color: 'var(--success)', bg: 'color-mix(in srgb, var(--success) 12%, transparent)' },
  bloqueada: { label: 'Bloqueada', icon: <IconBan size={14} />, color: 'var(--danger)', bg: 'color-mix(in srgb, var(--danger) 12%, transparent)' },
}

const PRIORIDAD_CONFIG: Record<string, { label: string; icon: React.ReactNode; color: string; bg: string }> = {
  baja: { label: 'Baja', icon: <IconArrowDown size={14} />, color: 'var(--text-muted)', bg: 'color-mix(in srgb, var(--text-muted) 12%, transparent)' },
  media: { label: 'Media', icon: <IconArrowRight size={14} />, color: 'var(--info)', bg: 'color-mix(in srgb, var(--info) 12%, transparent)' },
  alta: { label: 'Alta', icon: <IconArrowUp size={14} />, color: 'var(--warning)', bg: 'color-mix(in srgb, var(--warning) 12%, transparent)' },
  urgente: { label: 'Urgente', icon: <IconAlertCircle size={14} />, color: 'var(--danger)', bg: 'color-mix(in srgb, var(--danger) 12%, transparent)' },
}

const APROBACION_CONFIG: Record<string, { label: string; icon: React.ReactNode; color: string }> = {
  pendiente: { label: 'Pendiente', icon: <IconClock size={13} />, color: 'var(--text-muted)' },
  enviada: { label: 'Enviada', icon: <IconMail size={13} />, color: 'var(--info)' },
  aprobada: { label: 'Aprobada', icon: <IconCheckCircle size={13} />, color: 'var(--success)' },
  rechazada: { label: 'Rechazada', icon: <IconXCircle size={13} />, color: 'var(--danger)' },
}

const COBRO_CONFIG: Record<string, { label: string; icon: React.ReactNode }> = {
  sin_costo: { label: 'Sin costo', icon: <IconDollar size={14} /> },
  monto_fijo: { label: 'Monto fijo', icon: <IconLock size={14} /> },
  por_horas: { label: 'Por horas', icon: <IconTimer size={14} /> },
}

export default function VerTarea() {
  const { id: proyectoId, tareaId } = useParams<{ id: string; tareaId: string }>()
  const db = useSupabase()

  const [tarea, setTarea] = useState<Tarea | null>(null)
  const [proyecto, setProyecto] = useState<Proyecto | null>(null)
  const [evidencias, setEvidencias] = useState<TareaEvidencia[]>([])
  const [tarifaCliente, setTarifaCliente] = useState<ConfigTarifaCliente | null>(null)
  const [loading, setLoading] = useState(true)
  const [showEditModal, setShowEditModal] = useState(false)
  const [savingHours, setSavingHours] = useState(false)

  // Quick-edit state for hours
  const [editHorasEst, setEditHorasEst] = useState<number>(0)
  const [editHorasReal, setEditHorasReal] = useState<number>(0)
  const [editFechaLimite, setEditFechaLimite] = useState<string>('')
  const [savingDate, setSavingDate] = useState(false)

  useEffect(() => {
    if (tarea) {
      setEditHorasEst(tarea.horas_estimadas)
      setEditHorasReal(tarea.horas_reales)
      setEditFechaLimite(tarea.fecha_limite?.split('T')[0] || '')
    }
  }, [tarea?.id, tarea?.horas_estimadas, tarea?.horas_reales, tarea?.fecha_limite])

  async function loadData() {
    if (!proyectoId || !tareaId) return
    setLoading(true)
    try {
      const [t, p] = await Promise.all([
        db.getById<Tarea>('tareas', Number(tareaId)),
        db.getById<Proyecto>('proyectos', Number(proyectoId)),
      ])
      setTarea(t)
      setProyecto(p)

      // Optional loads
      try {
        const allEv = await db.getAll<TareaEvidencia>('tarea_evidencias')
        setEvidencias(allEv.filter(e => e.tarea_id === Number(tareaId)))
      } catch { /* ok */ }

      if (p) {
        try {
          const allTarifas = await db.getAll<ConfigTarifaCliente>('config_tarifa_cliente')
          setTarifaCliente(allTarifas.find(tc => tc.cliente_id === p.cliente_id) ?? null)
        } catch { /* ok */ }
      }
    } catch (err) {
      console.error('Error loading task:', err)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => { loadData() }, [proyectoId, tareaId]) // eslint-disable-line react-hooks/exhaustive-deps

  /** Quick-save hours (and auto-calc monto for por_horas) */
  async function saveHours(newEst: number, newReal: number) {
    if (!tarea) return
    setSavingHours(true)
    try {
      const updates: Record<string, number> = {
        horas_estimadas: newEst,
        horas_reales: newReal,
      }
      // Auto-calculate monto if tipo_cobro is por_horas
      if (tarea.tipo_cobro === 'por_horas' && tarifaCliente && tarifaCliente.tarifa_hora > 0) {
        updates.monto = r2(newEst * tarifaCliente.tarifa_hora)
      }
      await db.update('tareas', { id: tarea.id, ...updates })
      setTarea({ ...tarea, ...updates })
    } catch {
      // Revert on error
      setEditHorasEst(tarea.horas_estimadas)
      setEditHorasReal(tarea.horas_reales)
    } finally {
      setSavingHours(false)
    }
  }

  function handleHoursBlur() {
    if (!tarea) return
    if (editHorasEst !== tarea.horas_estimadas || editHorasReal !== tarea.horas_reales) {
      saveHours(editHorasEst, editHorasReal)
    }
  }

  function handleHoursKeyDown(e: React.KeyboardEvent) {
    if (e.key === 'Enter') {
      (e.target as HTMLInputElement).blur()
    }
  }

  async function saveFechaLimite(value: string) {
    if (!tarea) return
    setSavingDate(true)
    try {
      const newVal = value || null
      await db.update('tareas', { id: tarea.id, fecha_limite: newVal })
      setTarea({ ...tarea, fecha_limite: newVal })
    } catch {
      setEditFechaLimite(tarea.fecha_limite?.split('T')[0] || '')
    } finally {
      setSavingDate(false)
    }
  }

  if (loading) return (
    <div className="page-enter" style={{ padding: '2rem' }}>
      <div style={{ color: 'var(--text-secondary)', marginTop: '2rem', textAlign: 'center' }}>
        <div className="auth-spinner" style={{ margin: '0 auto 12px' }} />Cargando tarea...
      </div>
    </div>
  )

  if (!tarea) return (
    <div className="page-enter">
      <div className="empty-state">
        <h3>Tarea no encontrada</h3>
        <Link to={`/proyectos/${proyectoId}`} className="btn btn-primary">
          <IconArrowLeft size={16} /> Volver al proyecto
        </Link>
      </div>
    </div>
  )

  const estado = ESTADO_CONFIG[tarea.estado] || ESTADO_CONFIG.pendiente
  const prio = PRIORIDAD_CONFIG[tarea.prioridad] || PRIORIDAD_CONFIG.media
  const cobro = COBRO_CONFIG[tarea.tipo_cobro] || COBRO_CONFIG.sin_costo
  const aprobacion = APROBACION_CONFIG[tarea.estado_aprobacion] || APROBACION_CONFIG.pendiente
  const isOverdue = tarea.fecha_limite && new Date(tarea.fecha_limite + 'T23:59:59') < new Date() && tarea.estado !== 'completada'
  const hasCost = tarea.tipo_cobro !== 'sin_costo'

  return (
    <div className="page-enter" id="page-ver-tarea">
      <PageHeader
        breadcrumbs={[
          { label: 'Proyectos', to: '/proyectos' },
          { label: proyecto?.nombre || 'Proyecto', to: `/proyectos/${proyectoId}` },
          { label: tarea.titulo },
        ]}
        meta={`#${tarea.id}`}
        actions={
          <button className="btn btn-secondary" onClick={() => setShowEditModal(true)}>
            <IconEdit size={16} /> Editar
          </button>
        }
      />

      {/* Content grid */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 320px', gap: 24, marginTop: 8 }}>
        {/* Main content */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
          {/* Status badges */}
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            <span style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '4px 12px', borderRadius: 20, fontSize: '0.82rem', fontWeight: 600, background: estado.bg, color: estado.color }}>
              {estado.icon} {estado.label}
            </span>
            <span style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '4px 12px', borderRadius: 20, fontSize: '0.82rem', fontWeight: 600, background: prio.bg, color: prio.color }}>
              {prio.icon} {prio.label}
            </span>
            {isOverdue && (
              <span style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '4px 12px', borderRadius: 20, fontSize: '0.82rem', fontWeight: 600, background: 'color-mix(in srgb, var(--danger) 12%, transparent)', color: 'var(--danger)' }}>
                <IconAlertTriangle size={13} /> Vencida
              </span>
            )}
          </div>

          {/* Description */}
          <div style={{ background: 'var(--bg-secondary)', border: '1px solid var(--border)', borderRadius: 10, padding: 20 }}>
            <h3 style={{ margin: '0 0 8px', fontSize: '0.9rem', color: 'var(--text-secondary)', display: 'flex', alignItems: 'center', gap: 6 }}>
              <IconMessageSquare size={16} /> Descripción
            </h3>
            {tarea.descripcion ? (
              <p style={{ margin: 0, lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>{tarea.descripcion}</p>
            ) : (
              <p style={{ margin: 0, color: 'var(--text-muted)', fontStyle: 'italic' }}>Sin descripción</p>
            )}
          </div>

          {/* Tags */}
          {tarea.etiquetas.length > 0 && (
            <div style={{ background: 'var(--bg-secondary)', border: '1px solid var(--border)', borderRadius: 10, padding: 20 }}>
              <h3 style={{ margin: '0 0 10px', fontSize: '0.9rem', color: 'var(--text-secondary)', display: 'flex', alignItems: 'center', gap: 6 }}>
                <IconTag size={16} /> Etiquetas
              </h3>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                {tarea.etiquetas.map(tag => (
                  <span key={tag} className="tag-chip">{tag}</span>
                ))}
              </div>
            </div>
          )}

          {/* Notes */}
          {tarea.notas && (
            <div style={{ background: 'var(--bg-secondary)', border: '1px solid var(--border)', borderRadius: 10, padding: 20 }}>
              <h3 style={{ margin: '0 0 8px', fontSize: '0.9rem', color: 'var(--text-secondary)', display: 'flex', alignItems: 'center', gap: 6 }}>
                <IconFile size={16} /> Notas internas
              </h3>
              <p style={{ margin: 0, lineHeight: 1.6, whiteSpace: 'pre-wrap', color: 'var(--text-secondary)' }}>{tarea.notas}</p>
            </div>
          )}

          {/* Evidencias */}
          {evidencias.length > 0 && (
            <div style={{ background: 'var(--bg-secondary)', border: '1px solid var(--border)', borderRadius: 10, padding: 20 }}>
              <h3 style={{ margin: '0 0 10px', fontSize: '0.9rem', color: 'var(--text-secondary)', display: 'flex', alignItems: 'center', gap: 6 }}>
                <IconPaperclip size={16} /> Evidencias ({evidencias.length})
              </h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {evidencias.map(ev => (
                  <a key={ev.id} href={ev.url} target="_blank" rel="noopener noreferrer" style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px', background: 'var(--bg-primary)', borderRadius: 6, border: '1px solid var(--border)', textDecoration: 'none', color: 'inherit', fontSize: '0.85rem' }}>
                    {ev.tipo.includes('image') ? <IconImage size={16} /> : <IconFile size={16} />}
                    <span style={{ flex: 1 }}>{ev.nombre}</span>
                    <span style={{ color: 'var(--text-muted)', fontSize: '0.78rem' }}>{formatDate(ev.created_at)}</span>
                  </a>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Side panel */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          {/* Billing */}
          <div style={{ background: 'var(--bg-secondary)', border: '1px solid var(--border)', borderRadius: 10, padding: 16 }}>
            <h3 style={{ margin: '0 0 12px', fontSize: '0.85rem', color: 'var(--text-secondary)', display: 'flex', alignItems: 'center', gap: 6 }}>
              <IconDollar size={16} /> Facturación
            </h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10, fontSize: '0.85rem' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ color: 'var(--text-secondary)' }}>Tipo de cobro</span>
                <span style={{ display: 'flex', alignItems: 'center', gap: 4, fontWeight: 500 }}>{cobro.icon} {cobro.label}</span>
              </div>
              {hasCost && (
                <>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span style={{ color: 'var(--text-secondary)' }}>Monto</span>
                    <span style={{ fontWeight: 700, color: 'var(--accent-primary)' }}>{formatCurrency(tarea.monto, 'USD')}</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span style={{ color: 'var(--text-secondary)' }}>Aprobación</span>
                    <span style={{ display: 'flex', alignItems: 'center', gap: 4, color: aprobacion.color, fontWeight: 600 }}>
                      {aprobacion.icon} {aprobacion.label}
                    </span>
                  </div>
                </>
              )}
              {/* Hours — always visible, inline editable */}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ color: 'var(--text-secondary)' }}>Horas estimadas</span>
                <input
                  type="number"
                  min={0}
                  step={0.5}
                  value={editHorasEst}
                  onChange={e => setEditHorasEst(Math.max(0, Number(e.target.value)))}
                  onBlur={handleHoursBlur}
                  onKeyDown={handleHoursKeyDown}
                  disabled={savingHours}
                  className="quick-edit-input"
                  title="Editar horas estimadas"
                />
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ color: 'var(--text-secondary)' }}>Horas reales</span>
                <input
                  type="number"
                  min={0}
                  step={0.5}
                  value={editHorasReal || ''}
                  placeholder="—"
                  onChange={e => setEditHorasReal(Math.max(0, Number(e.target.value)))}
                  onBlur={handleHoursBlur}
                  onKeyDown={handleHoursKeyDown}
                  disabled={savingHours}
                  className={`quick-edit-input${editHorasReal > editHorasEst && editHorasEst > 0 ? ' quick-edit-danger' : ''}`}
                  title="Editar horas reales"
                />
              </div>
              {tarea.factura_id && (
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{ color: 'var(--text-secondary)' }}>Factura</span>
                  <Link to={`/facturas/${tarea.factura_id}`} style={{ color: 'var(--accent-primary)', fontWeight: 600 }}>
                    Ver factura
                  </Link>
                </div>
              )}
              {tarifaCliente && tarifaCliente.tarifa_hora > 0 && (
                <div style={{ borderTop: '1px solid var(--border)', paddingTop: 8, display: 'flex', justifyContent: 'space-between' }}>
                  <span style={{ color: 'var(--text-muted)', fontSize: '0.8rem' }}>Tarifa cliente</span>
                  <span style={{ fontSize: '0.8rem' }}>{formatCurrency(tarifaCliente.tarifa_hora, tarifaCliente.moneda)}/h</span>
                </div>
              )}
              {/* Auto-calculated cost preview */}
              {tarea.tipo_cobro === 'por_horas' && tarifaCliente && tarifaCliente.tarifa_hora > 0 && (
                <div style={{ borderTop: '1px solid var(--border)', paddingTop: 8, display: 'flex', justifyContent: 'space-between' }}>
                  <span style={{ color: 'var(--text-muted)', fontSize: '0.8rem' }}>Costo calculado</span>
                  <span style={{ fontSize: '0.8rem', fontWeight: 600, color: 'var(--accent-primary)' }}>{formatCurrency(r2(editHorasEst * tarifaCliente.tarifa_hora), tarifaCliente.moneda)}</span>
                </div>
              )}
            </div>
          </div>

          {/* Dates */}
          <div style={{ background: 'var(--bg-secondary)', border: '1px solid var(--border)', borderRadius: 10, padding: 16 }}>
            <h3 style={{ margin: '0 0 12px', fontSize: '0.85rem', color: 'var(--text-secondary)', display: 'flex', alignItems: 'center', gap: 6 }}>
              <IconCalendar size={16} /> Fechas
            </h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, fontSize: '0.85rem' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ color: 'var(--text-secondary)' }}>Fecha límite</span>
                <input
                  type="date"
                  value={editFechaLimite}
                  onChange={e => setEditFechaLimite(e.target.value)}
                  onBlur={e => {
                    const current = tarea.fecha_limite?.split('T')[0] || ''
                    if (e.target.value !== current) saveFechaLimite(e.target.value)
                  }}
                  disabled={savingDate}
                  className="quick-edit-input"
                  style={{ width: 130, textAlign: 'right', cursor: 'pointer' }}
                  title="Editar fecha límite"
                />
              </div>
              {tarea.fecha_completada && (
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span style={{ color: 'var(--text-secondary)' }}>Completada</span>
                  <span>{formatDate(tarea.fecha_completada)}</span>
                </div>
              )}
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span style={{ color: 'var(--text-secondary)' }}>Creada</span>
                <span>{tarea.created_at ? formatDate(tarea.created_at) : '—'}</span>
              </div>
            </div>
          </div>

          {/* Metadata */}
          <div style={{ background: 'var(--bg-secondary)', border: '1px solid var(--border)', borderRadius: 10, padding: 16 }}>
            <h3 style={{ margin: '0 0 12px', fontSize: '0.85rem', color: 'var(--text-secondary)', display: 'flex', alignItems: 'center', gap: 6 }}>
              <IconBarChart size={16} /> Detalles
            </h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, fontSize: '0.85rem' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span style={{ color: 'var(--text-secondary)' }}>ID</span>
                <span style={{ fontFamily: 'monospace' }}>#{tarea.id}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span style={{ color: 'var(--text-secondary)' }}>Orden</span>
                <span>{tarea.orden}</span>
              </div>
              {proyecto && (
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{ color: 'var(--text-secondary)' }}>Proyecto</span>
                  <Link to={`/proyectos/${proyecto.id}`} style={{ color: 'var(--accent-primary)', fontWeight: 500 }}>{proyecto.nombre}</Link>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Edit Modal */}
      {showEditModal && proyecto && (
        <TareaModal
          tarea={tarea}
          proyectoId={proyecto.id}
          clienteId={proyecto.cliente_id}
          tarifaCliente={tarifaCliente}
          facturas={[]}
          onClose={() => setShowEditModal(false)}
          onSaved={() => { setShowEditModal(false); loadData() }}
          onDeleted={() => { setShowEditModal(false); window.history.back() }}
        />
      )}
    </div>
  )
}
